import { signInWithCustomToken } from "firebase/auth";
const DEFAULT_SSO_LOGIN_URL = "https://accounts.augmoto.com/login";
/**
 * sessionStorage key tracking how many times we've bounced this tab to the
 * central login page without ever establishing a session. Reset to zero the
 * instant a session is confirmed (see clearSsoRedirectState).
 */
const SSO_ATTEMPT_KEY = "augmodo.sso.redirectAttempts";
/**
 * How many login round-trips to tolerate before giving up and letting the app
 * surface an error instead of redirecting forever. A healthy flow uses exactly
 * one; the budget leaves room for a user to fumble the Google picker or hit
 * Back a couple of times. Only a structural break (mismatched Firebase project,
 * unreachable mint service, clock skew) exhausts it, and bouncing again won't
 * fix that — so we stop and surface a recoverable error instead.
 */
const MAX_SSO_ATTEMPTS = 5;
/**
 * Complete an SSO round-trip: if the central login page handed us a custom
 * token in the URL fragment, sign the local Firebase session in with it.
 *
 * Call this once on app boot, BEFORE checking auth state.
 *
 * Returns:
 *  - "no-token"  if there was no token to consume (the normal first-load case);
 *  - "signed-in" if a token was found and sign-in succeeded;
 *  - "failed"    if a token was found but sign-in threw. The caller MUST treat
 *                this as terminal and show an error rather than redirecting back
 *                to SSO — the token is already spent and stripped from the URL,
 *                so another round-trip would mint a fresh token that fails the
 *                same way, producing an infinite redirect loop.
 */
export async function consumeSsoToken(auth) {
    const match = window.location.hash.match(/sso_token=([^&]+)/);
    if (!match)
        return "no-token";
    // Scrub the token from the address bar and history before anything else can
    // observe or persist it.
    history.replaceState(null, "", window.location.pathname + window.location.search);
    try {
        await signInWithCustomToken(auth, decodeURIComponent(match[1]));
        clearSsoRedirectState();
        return "signed-in";
    }
    catch (error) {
        console.error("SSO sign-in failed:", error);
        return "failed";
    }
}
/**
 * Send an unauthenticated user to the central login page, with the current
 * URL (sans fragment) as the return address.
 *
 * Pass `import.meta.env.VITE_SSO_LOGIN_URL` from the app to allow local-dev
 * overrides; the env var can't be read here because Vite doesn't substitute
 * import.meta.env inside prebuilt workspace packages.
 *
 * Returns `false` WITHOUT navigating once we've already bounced through SSO
 * MAX_SSO_ATTEMPTS times this session without signing in. The caller should
 * surface an error in that case — see consumeSsoToken's "failed" note.
 */
export function redirectToSso(loginUrl) {
    const attempts = Number(sessionStorage.getItem(SSO_ATTEMPT_KEY) ?? "0");
    if (attempts >= MAX_SSO_ATTEMPTS)
        return false;
    sessionStorage.setItem(SSO_ATTEMPT_KEY, String(attempts + 1));
    const here = window.location.href.split("#")[0];
    const url = loginUrl || DEFAULT_SSO_LOGIN_URL;
    window.location.assign(`${url}?redirect_uri=${encodeURIComponent(here)}`);
    return true;
}
/**
 * Clear the redirect-attempt backstop. Call this the moment a session is
 * confirmed so the next genuine sign-out can redirect with a clean budget.
 */
export function clearSsoRedirectState() {
    sessionStorage.removeItem(SSO_ATTEMPT_KEY);
}
const LOGOUT_KEY = "augmodo.sso.intentionalLogout";
/**
 * Signal that the user is deliberately signing out. Call this BEFORE
 * auth.signOut() so SsoGate can read the flag when the auth-state-change
 * fires and redirect to the SSO login page with ?logout=1 instead of
 * immediately auto-signing back in.
 */
export function markIntentionalLogout() {
    sessionStorage.setItem(LOGOUT_KEY, "1");
}
/**
 * Read and clear the intentional-logout flag. Returns true once per
 * deliberate sign-out; false on any other auth-state-change to null.
 */
export function consumeIntentionalLogout() {
    const set = sessionStorage.getItem(LOGOUT_KEY) === "1";
    sessionStorage.removeItem(LOGOUT_KEY);
    return set;
}
/**
 * Redirect to the SSO login page with ?logout=1 so it clears its own
 * session rather than auto-signing the user back in. Falls back to the
 * default accounts.augmoto.com URL when loginUrl is not provided, matching
 * the behaviour of redirectToSso.
 */
export function redirectToSsoLogout(loginUrl) {
    const here = window.location.href.split("#")[0];
    const url = loginUrl || DEFAULT_SSO_LOGIN_URL;
    window.location.assign(`${url}?redirect_uri=${encodeURIComponent(here)}&logout=1`);
}
/** Resolve the current Firebase user once auth state is known. */
export function getCurrentUser(auth) {
    return new Promise((resolve) => {
        const remove = auth.onAuthStateChanged((user) => {
            remove();
            resolve(user);
        });
    });
}
